import { TransferAPI } from '../api/transfer';

import { MultipartChunk } from '../../models/multipart-chunk';

import Logger from '@module/logger';

/**
 * Given a transfer object and one file, it splits the file into chunks,
 * queues them to upload, and finalize the file once all chunks have been uploaded.
 * @param   {String} transferId Transfer unique identifier
 * @param   {Object} file       File entry object
 * @param   {Object} queue      Chunks queue
 * @returns {Promise}
 */
export function uploadFile(transferId, file, queue) {
  Logger.log(`[${file.name}] Starting file upload`);
  Logger.log(`[${file.name}] Creating multipart chunks`);

  const chunks = createChunksForFile(transferId, file);

  Logger.log(`[${file.name}] Queue all multipart chunks`);
  return uploadChunksAndFinalize(transferId, file, chunks, queue);
}

/**
 * Given a transfer object and a file entry, it splits the file into chunks
 * and creates a "task" ready to be processed by the chunk queue worker.
 * @param   {String} transferId Transfer unique identifier
 * @param   {Object} file       File entry object
 * @returns {Array}  chunks     A list of chunks ready to be uploaded
 */
function createChunksForFile(transferId, file) {
  const chunks = [];
  for (
    let partNumber = 0;
    partNumber < file.multipart.numberOfChunks;
    partNumber++
  ) {
    chunks.push(createChunk(transferId, file, partNumber));
  }

  return chunks;
}

/**
 * Enqueue all the givens chunks, wait for them to be uploaded and
 * finalize the file. Upload missing chunks again if some of them
 * fail to be propcessed by our backend system.
 * @param   {String}  transferId Transfer unique identifier
 * @param   {Object}  file       Item containing a list of chunks to be uploaded.
 * @param   {Array}   chunks     A list of chunks ready to be uploaded
 * @param   {Object}  queue      Chunks queue
 * @returns {Promise}
 */
async function uploadChunksAndFinalize(transferId, file, chunks, queue) {
  await Promise.all(chunks.map((chunk) => queue.push(chunk)));

  Logger.log(`[${file.name}] Finalize file object`);
  const finalized = await TransferAPI.finalizeFileObject(transferId, file.id, {
    chunk_count: file.multipart.numberOfChunks,
  });

  if (finalized.missing_chunks) {
    Logger.log(
      `[${file.name}] Missing ${finalized.missing_chunks.join(', ')} chunks`
    );
    // Some chunks might not be processed properly by our backend systems,
    // and when we try to finalize the file, we can recieve a list of missing chunks
    // that must be uploaded again. Queue these chunks only.
    return uploadChunksAndFinalize(
      transferId,
      file,
      createMissingChunks(transferId, file, finalized.missing_chunks),
      queue
    );
  }
}

/**
 * Crete multipart chunks only for the given missing parts.
 * @param   {String} transferId   Transfer unique identifier
 * @param   {Object} file         File entry object
 * @param   {Array}  missingParts A list of missing parts numbers
 * @returns {Array}  chunks       A list of missing chunks ready to be uploaded
 */
function createMissingChunks(transferId, file, missingParts) {
  return missingParts.map((partNumber) =>
    createChunk(transferId, file, partNumber - 1)
  );
}

/**
 * Create a multipart chunk representation for a given part number.
 * It will contain all the necessary information to upload a chunk,
 * including the content of the chunk to be uploaded.
 * @param   {String} transferId Transfer object
 * @param   {Object} file       File entry object
 * @param   {Number} partNumber
 * @returns {Object} chunk
 */
function createChunk(transferId, file, partNumber) {
  const sliceMethod =
    file.ref.slice || file.ref.mozSlice || file.ref.webkitSlice;

  const chunkStart = partNumber * file.multipart.chunkSize;
  const chunkEnd = (partNumber + 1) * file.multipart.chunkSize;

  // TODO: initialize this progress array when the file is created
  file.state.bytesProgress[partNumber] = 0;

  return new MultipartChunk(
    transferId,
    file,
    partNumber,
    sliceMethod.call(file.ref, chunkStart, chunkEnd)
  );
}
